import {
  HeatingSystem,
  HeatingSystemMetadataResponse,
  MetadataKeys,
} from '@/features/core/store/page-modules/heating-systems-metadata/heating-systems-metadata.module';
import { HeatingSystemCollectionWithPaginationQuery_heatingSystemsList_items } from '@/hsc-api/queries/__generated__/HeatingSystemCollectionWithPaginationQuery';

export const HEATING_SYSTEM_COMPONENT_TYPE_FILTERS: Record<string, string> = {
  BOILER: 'BOILER',
  TELE_HEATING: 'TELE_HEATING',
  TELE_HEATING_SECONDARY: 'TELE_HEATING_SECONDARY',
  HEATING_CONTROL: 'HEATING_CONTROL',
  COMBINED_HEAT_POWER_PLANT: 'COMBINED_HEAT_POWER_PLANT',
  HEAT_PUMP_PRIMARY: 'HEAT_PUMP_PRIMARY',
  HEAT_PUMP_SECONDARY: 'HEAT_PUMP_SECONDARY',
  SOLAR_HEATING: 'SOLAR_HEATING',
};

export function removeLabelFromName(measurementName: string | null, metricLabel: string): string {
  if (!measurementName) return 'Unknown name';

  // match the metricLabel from the end of the measurementName
  const pattern = new RegExp(`${metricLabel}$`, 'g');

  return measurementName.replace(pattern, '').trim();
}

export function getHeatingSystemsComponents(heatingSystem: HeatingSystem): Map<string, string> {
  const heatingSystemComponents = new Map<string, string>(
    heatingSystem.components.heatingSystemMeasurementGroups.flatMap(({ heatingSystemMeasurements }) => {
      return heatingSystemMeasurements.map((measurement) => {
        return [measurement.spotId, removeLabelFromName(measurement.measurementName, measurement.metricLabel)];
      });
    }),
  );

  return heatingSystemComponents;
}

type ComponentType = {
  component: string;
  type: string;
  groupName: string;
};

export function getHeatingSystemsComponentsWithTypes(heatingSystem: HeatingSystem): Map<string, ComponentType> {
  const heatingSystemComponents = new Map<string, ComponentType>(
    heatingSystem.components.heatingSystemMeasurementGroups.flatMap(
      ({ heatingSystemMeasurements, systemComponentTypeName, groupName }) => {
        return heatingSystemMeasurements.map((measurement) => {
          const component = removeLabelFromName(measurement.measurementName, measurement.metricLabel);
          return [measurement.spotId, { component, type: systemComponentTypeName, groupName }];
        });
      },
    ),
  );

  return heatingSystemComponents;
}

export enum GK4HeizungsprofiMetadataStatus {
  MANDATORY_INFORMATION_COMPLETE = 'MANDATORY_INFORMATION_COMPLETE',
  MANDATORY_INFORMATION_INCOMPLETE = 'MANDATORY_INFORMATION_INCOMPLETE',
}

export enum ATRegulationMetadataStatus {
  TRUE = 'JA',
  FALSE = 'NEIN',
}

export type HeatingSystemsAndSpotsResult = {
  spotIds: Set<string>;
  heatingSystems: HeatingSystem[];
};

export function getHeatingSystemsAndSpots(
  heatingSystems: HeatingSystem[],
  editableRegisterNames: string[],
): HeatingSystemsAndSpotsResult {
  const spotIds = new Set<string>();
  const heatingSystemsWithEditableRegisters: HeatingSystem[] = [];

  heatingSystems.forEach((heatingSystem) => {
    let hasEditable = false;
    heatingSystem.components.heatingSystemMeasurementGroups.forEach((group) => {
      group.heatingSystemMeasurements.forEach((measurement) => {
        if (editableRegisterNames.includes(measurement.metricName.toLowerCase())) {
          spotIds.add(measurement.spotId);
          hasEditable = true;
        }
      });
    });
    if (hasEditable) {
      heatingSystemsWithEditableRegisters.push(heatingSystem);
    }
  });
  return {
    spotIds,
    heatingSystems: heatingSystemsWithEditableRegisters,
  };
}

// eslint-disable-next-line complexity
export function isComponentMetadataCompleted(component: HeatingSystemMetadataResponse): boolean {
  const metadata = component.metadata as MetadataKeys;
  switch (component.componentType) {
    case 'BOILER':
      if (typeof metadata.condensing === 'boolean') {
        return true;
      }
      return false;
    case 'WARM_HEATING':
    case 'WATER_HEATING':
    case 'POTABLE_WATER_HEATING':
      if (!metadata.type_of_pwh) {
        return false;
      }
      return true;
    case 'TELE_HEATING':
      if (!metadata.flow_temperature_control_parameter.type || !metadata.return_temperature_control_parameter.type) {
        return false;
      } else if (metadata.flow_temperature_control_parameter.type === 'pointed_curve') {
        const pointedCurveParameter = metadata.flow_temperature_control_parameter.pointed_curve_parameter;
        if (!pointedCurveParameter || pointedCurveParameter.length < 2) {
          return false;
        }
      } else if (metadata.return_temperature_control_parameter.type === 'pointed_curve') {
        const pointedCurveParameter = metadata.return_temperature_control_parameter.pointed_curve_parameter;
        if (!pointedCurveParameter || pointedCurveParameter.length < 2) {
          return false;
        }
      }
      return true;
    case 'HEAT_PUMP_PRIMARY':
    case 'HEAT_PUMP_SECONDARY':
      if (
        !metadata.energy_source_type ||
        !metadata.heat_sink_type ||
        typeof metadata.supply_potable_water_heating !== 'boolean'
      ) {
        return false;
      }
      return true;
    case 'HEAT_EXCHANGER_PRIMARY':
    case 'HEAT_EXCHANGER_SECONDARY':
      if (!metadata.terminal_temperature_difference) {
        return false;
      }
      return true;
    default:
      return true;
  }
}

export interface EnerIQDescription {
  de: string;
  en: string;
}

export interface EnerIQSuggestion {
  score: {
    agent_id: string;
    atoms: string[];
    component_type: string;
    identifier: string;
    metadata: {
      reason: {
        agent: string;
        operations: {
          [key: string]: {
            description: string | EnerIQDescription;
            exceptions: string[];
            reasons: Record<string, never> | null;
            severity: number;
            status_code: string;
          };
        };
      };
    };
    score: number;
    time: string;
  };
}

export interface EnerIQSuggestionList {
  coomponentType: string;
  spotId: string;
  suggestion: EnerIQSuggestion;
}

export type HeatingSystemComponentType =
  | 'boiler'
  | 'teleheating'
  | 'tele_heating'
  | 'tele_heating_secondary'
  | 'district_heating'
  | 'heating circuit'
  | 'heating_circuit'
  | 'water heating'
  | 'water_heating'
  | 'warm_heating'
  | 'potable_water_heating'
  | 'buffer'
  | 'gas_meter'
  | 'heating_control'
  | 'cogeneration_unit'
  | 'heat_pump'
  | 'heat_pump_primary'
  | 'heat_pump_secondary'
  | 'heat_exchanger'
  | 'heat_exchanger_primary'
  | 'heat_exchanger_secondary'
  | 'outside_temperature'
  | 'heat_meter_counter'
  | 'unknown'
  | 'solar_heating'
  | 'combined_heat_power_plant'
  | 'mischer';

export const HEATING_SYSTEM_COMPONENT_NAME_MAP: Record<HeatingSystemComponentType, string> = {
  boiler: 'Kessel',
  teleheating: 'Fernwärme',
  tele_heating: 'Fernwärme',
  tele_heating_secondary: 'Fernwärme Sekundärseite',
  district_heating: 'Fernwärme',
  'heating circuit': 'Heizkreis',
  heating_circuit: 'Heizkreis',
  'water heating': 'Warmwasseraufbereitung',
  water_heating: 'Warmwasseraufbereitung',
  warm_heating: 'Warmwasseraufbereitung',
  potable_water_heating: 'Warmwasseraufbereitung',
  buffer: 'Pufferspeicher',
  gas_meter: 'Gaszähler',
  heating_control: 'Heizungssteuerung',
  unknown: 'Unbekannt',
  cogeneration_unit: 'BHKW',
  heat_pump: 'Wärmepumpe',
  heat_pump_primary: 'Wärmepumpe Primäre',
  heat_pump_secondary: 'Wärmepumpe Sekundäre',
  heat_exchanger: 'Wärmetauscher',
  heat_exchanger_primary: 'Wärmetauscher Primäre',
  heat_exchanger_secondary: 'Wärmetauscher Sekundäre',
  outside_temperature: 'Außentemperatur',
  heat_meter_counter: 'Wärmezähler',
  solar_heating: 'Solarthermieanlage',
  combined_heat_power_plant: 'BHKW',
  mischer: 'Mischer',
} as const;

export function getHeatingSystemComponentName(componentType: string): string {
  const normalizedType = componentType.toLowerCase() as HeatingSystemComponentType;
  return HEATING_SYSTEM_COMPONENT_NAME_MAP[normalizedType] ?? '';
}

/**
 * List of component display names to exclude, always in sync with HEATING_SYSTEM_COMPONENT_NAME_MAP and HeatingSystemComponentType.
 * Update the keys in this array to exclude more/less types.
 */
export type HeatingSystemComponentKeyUpperCase = Uppercase<keyof typeof HEATING_SYSTEM_COMPONENT_NAME_MAP>;
export const excludeHeatingSystemComponentNameList: Array<HeatingSystemComponentKeyUpperCase> = [
  'GAS_METER',
  'OUTSIDE_TEMPERATURE',
  'HEAT_METER_COUNTER',
  'HEATING_CONTROL',
];

export enum SeverityColor {
  GREEN1 = 'GREEN1',
  GREEN = 'GREEN',
  RED = 'RED',
  YELLOW = 'YELLOW',
  GRAY = 'GRAY',
}

export function getEnerIQSeverityColor(severity: number): SeverityColor {
  if (severity >= 0 && severity <= 100) {
    // very good operating state
    if (severity == 100) return SeverityColor.GREEN;
    // good operating state
    if (severity > 70 || severity <= 99) return SeverityColor.GREEN1;
    // attention is needed
    if (severity > 0 || severity <= 70) return SeverityColor.YELLOW;
    // critical state
    if (severity == 0) return SeverityColor.RED;
  }
  // analysis has no result (lack of data, missing sensors etc. and result is negative)
  return SeverityColor.GRAY;
}

export enum HeatingSystemType {
  Kessel = 'Kessel',
  Fernwärme = 'Fernwärme',
  BHKW = 'BHKW',
  Wärmepumpe = 'Wärmepumpe',
  Solarthermieanlage = 'Solarthermieanlage',
}

const commonTypes = [
  'WARM_HEATING',
  'HEATING_CIRCUIT',
  'UNKNOWN',
  'BUFFER',
  'BUFFER_SECONDARY',
  'HEATING_CONTROL',
  'HEAT_EXCHANGER_PRIMARY',
  'HEAT_EXCHANGER_SECONDARY',
];

export function kesselTypes(): string[] {
  return [HEATING_SYSTEM_COMPONENT_TYPE_FILTERS.BOILER, ...commonTypes];
}

export function fernwärmeTypes(): string[] {
  return [
    HEATING_SYSTEM_COMPONENT_TYPE_FILTERS.TELE_HEATING,
    HEATING_SYSTEM_COMPONENT_TYPE_FILTERS.TELE_HEATING_SECONDARY,
    ...commonTypes,
  ];
}

export function bhkwTypes(): string[] {
  return [HEATING_SYSTEM_COMPONENT_TYPE_FILTERS.COMBINED_HEAT_POWER_PLANT, ...commonTypes];
}

export function wärmepumpeTypes(): string[] {
  return [
    HEATING_SYSTEM_COMPONENT_TYPE_FILTERS.HEAT_PUMP_PRIMARY,
    HEATING_SYSTEM_COMPONENT_TYPE_FILTERS.HEAT_PUMP_SECONDARY,
    ...commonTypes,
  ];
}

export function solarthermieTypes(): string[] {
  return [HEATING_SYSTEM_COMPONENT_TYPE_FILTERS.SOLAR_HEATING, ...commonTypes];
}

export function filterHeatingSystemsByType(
  systems: HeatingSystemCollectionWithPaginationQuery_heatingSystemsList_items[],
  type: HeatingSystemType | null,
): HeatingSystemCollectionWithPaginationQuery_heatingSystemsList_items[] {
  if (!systems || systems.length === 0) return [];
  if (!type) return systems;

  const componentTypesByHeatingSystemType: Partial<Record<HeatingSystemType, string[]>> = {
    [HeatingSystemType.Kessel]: [HEATING_SYSTEM_COMPONENT_TYPE_FILTERS.BOILER],
    [HeatingSystemType.Fernwärme]: [
      HEATING_SYSTEM_COMPONENT_TYPE_FILTERS.TELE_HEATING,
      HEATING_SYSTEM_COMPONENT_TYPE_FILTERS.TELE_HEATING_SECONDARY,
      HEATING_SYSTEM_COMPONENT_TYPE_FILTERS.HEATING_CONTROL,
    ],
    [HeatingSystemType.BHKW]: [HEATING_SYSTEM_COMPONENT_TYPE_FILTERS.COMBINED_HEAT_POWER_PLANT],
    [HeatingSystemType.Wärmepumpe]: [
      HEATING_SYSTEM_COMPONENT_TYPE_FILTERS.HEAT_PUMP_PRIMARY,
      HEATING_SYSTEM_COMPONENT_TYPE_FILTERS.HEAT_PUMP_SECONDARY,
    ],
    [HeatingSystemType.Solarthermieanlage]: [HEATING_SYSTEM_COMPONENT_TYPE_FILTERS.SOLAR_HEATING],
  };

  const componentTypes = componentTypesByHeatingSystemType[type];

  if (!componentTypes) return systems;

  if (type === HeatingSystemType.Fernwärme) {
    // Exclude any CSV1 heating control components
    return systems.filter(
      (hs) =>
        !hs.heatingSystemMeasurementGroups?.some((g) => g.groupName.includes('Heizungssteuerung')) &&
        hs.heatingSystemMeasurementGroups?.some((g) => componentTypes.includes(g.systemComponentTypeName)),
    );
  }

  return systems.filter((hs) =>
    hs.heatingSystemMeasurementGroups?.some((g) => componentTypes.includes(g.systemComponentTypeName)),
  );
}
